<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" scrollable max-width="720">
    <template v-if="!hideButton" v-slot:activator="{ props }">
      <v-btn
        color="primary"
        :class="{ 'float-right': floatRight, 'mx-2': floatRight }"
        :disabled="!storeId || !canSell"
        v-bind="props"
      >
        <v-icon class="mr-2">mdi-account-cash</v-icon>
        Venda identificada
      </v-btn>
    </template>
    <v-card>
      <v-toolbar color="primary" :title="toolbarTitle">
        <v-btn color="black" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-progress-linear v-if="requestingApi || loadingCatalog" indeterminate color="primary" />
      <v-card-text>
        <!-- FORM -->
        <template v-if="step === 'form'">
          <v-alert
            v-if="!canSell"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            Esta loja precisa de Pix e/ou Cobrança no sistema ativos (TAP só no app). Ative em
            Editar loja.
          </v-alert>

          <div class="text-subtitle-2 mb-2">Itens</div>
          <v-row dense class="mb-2">
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="addProductId"
                :items="productItems"
                item-title="title"
                item-value="value"
                label="Produto da loja"
                density="compact"
                clearable
                hide-details="auto"
                :disabled="requestingApi"
                :loading="loadingCatalog"
                no-data-text="Nenhum produto ativo nesta loja"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-if="variantItems.length"
                v-model="addVariantId"
                :items="variantItems"
                item-title="title"
                item-value="value"
                label="Variação"
                density="compact"
                clearable
                hide-details="auto"
                :disabled="requestingApi"
              />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field
                v-model.number="addQty"
                label="Qtd"
                type="number"
                min="0.001"
                step="1"
                density="compact"
                hide-details="auto"
                :disabled="requestingApi"
              />
            </v-col>
            <v-col cols="6" md="1" class="d-flex align-center">
              <v-btn
                color="primary"
                variant="tonal"
                block
                :disabled="requestingApi || !addProductId || !(addQty > 0)"
                @click="addLine"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="cartHeaders"
            :items="cart"
            :items-per-page="-1"
            hide-default-footer
            density="compact"
            class="elevation-0 mb-4"
          >
            <template v-slot:item.name="{ item }">
              <span>{{ item.name }}</span>
              <span v-if="item.variant_label" class="text-medium-emphasis">
                · {{ item.variant_label }}
              </span>
            </template>
            <template v-slot:item.unit_price="{ value }">
              {{ formatPrice(value) }}
            </template>
            <template v-slot:item.subtotal="{ item }">
              {{ formatPrice(lineSubtotal(item)) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon variant="text" size="small" color="error" @click="removeLine(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              <div class="text-center text-medium-emphasis py-4">Adicione ao menos um item</div>
            </template>
          </v-data-table>
          <div class="text-right text-subtitle-1 font-weight-medium mb-4">
            Total: {{ formatPrice(cartTotal) }}
          </div>

          <div class="text-subtitle-2 mb-2">Beneficiário (membro)</div>
          <v-autocomplete
            v-model="beneficiaryId"
            v-model:search="memberSearch"
            :items="memberItems"
            item-title="title"
            item-value="value"
            label="Membro do grupo"
            density="compact"
            clearable
            hide-details="auto"
            no-filter
            :loading="loadingMembers"
            :disabled="requestingApi"
            class="mb-3"
            :no-data-text="memberNoDataText"
          />

          <v-switch
            v-model="otherPayer"
            label="Outro pagador (PIX com documento de outra pessoa)"
            color="primary"
            density="compact"
            hide-details
            class="mb-2"
            :disabled="requestingApi"
          />
          <v-autocomplete
            v-if="otherPayer"
            v-model="payerId"
            v-model:search="payerSearch"
            :items="payerItems"
            item-title="title"
            item-value="value"
            label="Pagador"
            density="compact"
            clearable
            hide-details="auto"
            no-filter
            :loading="loadingPayers"
            :disabled="requestingApi"
            class="mb-3"
          />

          <v-text-field
            v-model="payerPhone"
            label="Telefone (recibo WhatsApp, opcional)"
            density="compact"
            clearable
            hide-details="auto"
            class="mb-4"
            :disabled="requestingApi"
          />

          <div class="text-subtitle-2 mb-2">Pagamento</div>
          <v-radio-group v-model="paymentRail" density="compact" hide-details class="mb-2">
            <v-radio
              v-if="store?.pix_enabled"
              value="pix"
              label="Pix agora"
              :disabled="requestingApi"
            />
            <v-radio
              v-if="store?.invoice_enabled"
              value="invoice"
              label="Cobrar depois (fatura no sistema)"
              :disabled="requestingApi"
            />
          </v-radio-group>
          <v-text-field
            v-if="paymentRail === 'invoice'"
            v-model="dueDate"
            label="Vencimento da fatura"
            type="date"
            density="compact"
            hide-details="auto"
            class="mb-2"
            :disabled="requestingApi"
          />
          <div class="text-caption text-medium-emphasis">
            TAP / SoftPOS só no app PDV. Aqui: Pix imediato ou cobrança em Minhas faturas do membro.
          </div>
        </template>

        <!-- PIX QR -->
        <template v-else-if="step === 'pix'">
          <v-alert type="info" variant="tonal" density="compact" class="mb-3">
            Venda #{{ createdSale?.id }} — {{ formatPrice(createdSale?.amount_total) }}. Escaneie o
            QR ou copie o código. Aguardando pagamento…
          </v-alert>
          <div v-if="pixImageSrc" class="text-center mb-3">
            <img :src="pixImageSrc" alt="QR Code Pix" style="max-width: 240px; width: 100%" />
          </div>
          <v-textarea
            v-if="pixCopyPaste"
            :model-value="pixCopyPaste"
            label="Pix copia e cola"
            readonly
            rows="3"
            density="compact"
            class="mb-2"
          />
          <div class="d-flex flex-wrap ga-2">
            <v-btn
              v-if="pixCopyPaste"
              color="primary"
              variant="tonal"
              @click="copyPix"
            >
              <v-icon class="mr-1">mdi-content-copy</v-icon>
              Copiar
            </v-btn>
            <v-btn color="secondary" variant="text" :loading="polling" @click="pollSaleOnce">
              <v-icon class="mr-1">mdi-refresh</v-icon>
              Verificar pagamento
            </v-btn>
          </div>
          <div v-if="pollStatus" class="text-caption mt-2 text-medium-emphasis">
            Status: {{ pollStatus }}
          </div>
        </template>

        <!-- DONE -->
        <template v-else-if="step === 'done'">
          <v-alert
            :type="doneAlertType"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ doneMessage }}
          </v-alert>
          <div v-if="createdSale?.id" class="text-body-2">
            <div>Venda #{{ createdSale.id }}</div>
            <div v-if="invoiceId">Fatura #{{ invoiceId }}</div>
            <div>Total: {{ formatPrice(createdSale.amount_total) }}</div>
            <div v-if="createdSale.beneficiary?.name">
              Beneficiário: {{ createdSale.beneficiary.name }}
            </div>
          </div>
        </template>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn v-if="step !== 'done'" variant="text" color="error" @click="close">
          {{ step === 'pix' ? 'Fechar' : 'Cancelar' }}
        </v-btn>
        <v-btn
          v-if="step === 'form'"
          color="success"
          :loading="requestingApi"
          :disabled="!canSubmit"
          @click="submit"
        >
          <v-icon class="mr-2">mdi-check</v-icon>
          {{ paymentRail === 'invoice' ? 'Gerar cobrança' : 'Criar e pagar com Pix' }}
        </v-btn>
        <v-btn v-if="step === 'done'" color="primary" @click="close">
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

function defaultDueDate() {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return d.toISOString().slice(0, 10)
}

function emptyState() {
  return {
    step: 'form',
    cart: [],
    addProductId: null,
    addVariantId: null,
    addQty: 1,
    beneficiaryId: null,
    otherPayer: false,
    payerId: null,
    payerPhone: '',
    paymentRail: null,
    dueDate: defaultDueDate(),
    createdSale: null,
    invoiceId: null,
    pix: null,
    pollStatus: '',
    doneMessage: '',
    doneAlertType: 'success',
  }
}

export default {
  props: {
    user: Object,
    store: Object,
    storeProducts: {
      type: Array,
      default: null,
    },
    floatRight: Boolean,
    hideButton: Boolean,
  },
  emits: ['created', 'paid', 'invoiced'],
  data() {
    return {
      dialog: false,
      requestingApi: false,
      loadingCatalog: false,
      loadingMembers: false,
      loadingPayers: false,
      products: [],
      variantsByProduct: {},
      members: [],
      payers: [],
      memberSearch: '',
      payerSearch: '',
      memberTimeout: null,
      payerTimeout: null,
      pollTimer: null,
      polling: false,
      cartHeaders: [
        { title: 'Produto', key: 'name', sortable: false },
        { title: 'Qtd', key: 'quantity', align: 'end', sortable: false },
        { title: 'Unit.', key: 'unit_price', align: 'end', sortable: false },
        { title: 'Subtotal', key: 'subtotal', align: 'end', sortable: false },
        { title: '', key: 'actions', align: 'end', sortable: false },
      ],
      ...emptyState(),
    }
  },
  computed: {
    storeId() {
      return this.store?.id != null ? Number(this.store.id) : null
    },
    canSell() {
      const s = this.store || {}
      return !!(s.pix_enabled || s.invoice_enabled)
    },
    toolbarTitle() {
      const name = this.store?.name
      return name ? `Venda identificada — ${name}` : 'Venda identificada'
    },
    productItems() {
      return (this.products || [])
        .filter((sp) => sp.active !== false)
        .map((sp) => {
          const p = sp.product || {}
          const id = sp.product_id || p.id
          const price =
            sp.price_override != null && sp.price_override !== ''
              ? sp.price_override
              : p.base_price
          const priceLabel =
            price != null
              ? Number(price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              : ''
          return {
            title: priceLabel ? `${p.name || `Produto #${id}`} (${priceLabel})` : p.name || `Produto #${id}`,
            value: id,
            raw: sp,
          }
        })
    },
    selectedStoreProduct() {
      const id = this.addProductId
      if (!id) return null
      return (this.products || []).find(
        (sp) => Number(sp.product_id || sp.product?.id) === Number(id)
      )
    },
    variantItems() {
      const pid = this.addProductId
      if (!pid) return []
      const list = this.variantsByProduct[pid] || []
      return list.map((v) => ({
        title: this.variantTitle(v),
        value: v.id,
      }))
    },
    memberItems() {
      return (this.members || []).map((lp) => ({
        title: this.lpTitle(lp),
        value: lp.id,
      }))
    },
    payerItems() {
      return (this.payers || []).map((lp) => ({
        title: this.lpTitle(lp),
        value: lp.id,
      }))
    },
    memberNoDataText() {
      if (this.loadingMembers) return 'Buscando…'
      if (!(this.memberSearch || '').trim()) return 'Digite para buscar membros do grupo'
      return 'Nenhum membro encontrado'
    },
    cartTotal() {
      return (this.cart || []).reduce((sum, line) => sum + this.lineSubtotal(line), 0)
    },
    canSubmit() {
      if (this.requestingApi || !this.canSell) return false
      if (!this.cart.length) return false
      if (!this.beneficiaryId) return false
      if (this.otherPayer && !this.payerId) return false
      if (!this.paymentRail) return false
      if (this.paymentRail === 'pix' && !this.store?.pix_enabled) return false
      if (this.paymentRail === 'invoice' && !this.store?.invoice_enabled) return false
      return true
    },
    pixCopyPaste() {
      return this.pix?.copy_paste || this.pix?.emv || null
    },
    pixImageSrc() {
      const b64 = this.pix?.qr_code_base64 || this.pix?.qr_code_base64_image
      if (b64) {
        if (String(b64).startsWith('data:')) return b64
        return `data:image/png;base64,${b64}`
      }
      const qr = this.pix?.qr_code
      if (qr && String(qr).startsWith('data:')) return qr
      if (qr && String(qr).startsWith('http')) return qr
      return null
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        this.resetForm()
        this.bootstrapCatalog()
        this.pickDefaultRail()
      } else {
        this.stopPoll()
      }
    },
    addProductId(id) {
      this.addVariantId = null
      if (id) this.loadVariants(id)
    },
    memberSearch(q) {
      if (this.memberTimeout) clearTimeout(this.memberTimeout)
      const text = (q || '').trim()
      if (!text) {
        this.loadingMembers = false
        return
      }
      this.loadingMembers = true
      this.memberTimeout = setTimeout(() => {
        this.memberTimeout = null
        this.searchMembers(text, 'members')
      }, 400)
    },
    payerSearch(q) {
      if (this.payerTimeout) clearTimeout(this.payerTimeout)
      const text = (q || '').trim()
      if (!text) {
        this.loadingPayers = false
        return
      }
      this.loadingPayers = true
      this.payerTimeout = setTimeout(() => {
        this.payerTimeout = null
        this.searchMembers(text, 'payers')
      }, 400)
    },
  },
  beforeUnmount() {
    this.stopPoll()
  },
  methods: {
    close() {
      this.dialog = false
    },
    resetForm() {
      Object.assign(this, emptyState())
      this.pickDefaultRail()
    },
    pickDefaultRail() {
      const s = this.store || {}
      if (s.pix_enabled) this.paymentRail = 'pix'
      else if (s.invoice_enabled) this.paymentRail = 'invoice'
      else this.paymentRail = null
    },
    bootstrapCatalog() {
      if (Array.isArray(this.storeProducts) && this.storeProducts.length) {
        this.products = this.storeProducts
        return
      }
      this.loadStoreProducts()
    },
    loadStoreProducts() {
      const vm = this
      if (!vm.storeId) return
      vm.loadingCatalog = true
      return api.storeProduct
        .list({ store_id: vm.storeId, itemsPerPage: 200, active: true })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.products = items || []
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.loadingCatalog = false
        })
    },
    loadVariants(productId) {
      const vm = this
      if (vm.variantsByProduct[productId]) return
      return api.productVariant
        .list({ product_id: productId, itemsPerPage: 100, active: true })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.variantsByProduct = {
            ...vm.variantsByProduct,
            [productId]: items || [],
          }
        })
        .catch(() => {
          vm.variantsByProduct = { ...vm.variantsByProduct, [productId]: [] }
        })
    },
    searchMembers(searchText, target) {
      const vm = this
      const groupId = api.storage.selectedGroup()?.id
      if (!groupId) {
        if (target === 'payers') vm.loadingPayers = false
        else vm.loadingMembers = false
        return
      }
      return api.legalPerson
        .list(
          {
            searchText,
            itemsPerPage: 30,
            member_group_id: Number(groupId),
            active: true,
          },
          1
        )
        .then((result) => {
          const { items } = normalizeListPayload(result)
          const list = items?.length
            ? items
            : Array.isArray(result?.data)
              ? result.data
              : Array.isArray(result)
                ? result
                : []
          if (target === 'payers') vm.payers = list
          else vm.members = list
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          if (target === 'payers') vm.loadingPayers = false
          else vm.loadingMembers = false
        })
    },
    lpTitle(lp) {
      if (!lp) return ''
      const ssn = lp.ssn ? ` · ${lp.ssn}` : ''
      return `${lp.name || `LP #${lp.id}`}${ssn}`
    },
    variantTitle(v) {
      if (!v) return '—'
      if (v.label) return v.label
      if (Array.isArray(v.options) && v.options.length) {
        return v.options.map((o) => o.value || o.name || o).join(' / ')
      }
      if (v.sku) return v.sku
      return `Var #${v.id}`
    },
    lineSubtotal(item) {
      return Number(item.quantity || 0) * Number(item.unit_price || 0)
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (isNaN(n)) return value
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    addLine() {
      const sp = this.selectedStoreProduct
      if (!sp || !(this.addQty > 0)) return
      const product = sp.product || {}
      const productId = sp.product_id || product.id
      const variants = this.variantsByProduct[productId] || []
      if (variants.length > 1 && !this.addVariantId) {
        this.$notify({ type: 'warn', text: 'Selecione a variação do produto' })
        return
      }
      let variantId = this.addVariantId
      if (!variantId && variants.length === 1) variantId = variants[0].id
      const unit =
        sp.price_override != null && sp.price_override !== ''
          ? Number(sp.price_override)
          : Number(product.base_price || 0)
      const key = `${productId}:${variantId || 'null'}`
      const existing = this.cart.find((c) => c.key === key)
      if (existing) {
        existing.quantity = Number(existing.quantity) + Number(this.addQty)
      } else {
        let variant_label = null
        if (variantId) {
          const v = variants.find((x) => x.id === variantId)
          variant_label = this.variantTitle(v)
        }
        this.cart.push({
          key,
          product_id: Number(productId),
          product_variant_id: variantId != null ? Number(variantId) : null,
          name: product.name || `Produto #${productId}`,
          variant_label,
          quantity: Number(this.addQty),
          unit_price: unit,
        })
      }
      this.addQty = 1
      this.addProductId = null
      this.addVariantId = null
    },
    removeLine(item) {
      this.cart = this.cart.filter((c) => c.key !== item.key)
    },
    buildItemsPayload() {
      return this.cart.map((c) => {
        const line = {
          product_id: c.product_id,
          quantity: c.quantity,
        }
        if (c.product_variant_id != null) {
          line.product_variant_id = c.product_variant_id
        }
        return line
      })
    },
    submit() {
      const vm = this
      if (!vm.canSubmit || vm.requestingApi) return
      vm.requestingApi = true
      const payload = {
        store_id: vm.storeId,
        items: vm.buildItemsPayload(),
        identity_mode: 'identified',
        beneficiary_legal_person_id: Number(vm.beneficiaryId),
        channel: 'web',
      }
      if (vm.otherPayer && vm.payerId) {
        payload.payer_legal_person_id = Number(vm.payerId)
      }
      if (vm.payerPhone) {
        payload.payer_phone = String(vm.payerPhone).trim()
      }

      return api.sale
        .create(payload)
        .then((sale) => {
          vm.createdSale = sale
          vm.$emit('created', sale)
          if (vm.paymentRail === 'invoice') {
            return api.sale
              .payInvoice(sale.id, vm.dueDate ? { due_date: vm.dueDate } : {})
              .then((result) => {
                vm.createdSale = result?.sale || sale
                vm.invoiceId = result?.invoice_id || result?.invoice?.id || null
                vm.doneMessage =
                  'Cobrança gerada. O membro paga depois em Minhas faturas; a venda fica aguardando pagamento.'
                vm.doneAlertType = 'success'
                vm.step = 'done'
                vm.$emit('invoiced', result)
                vm.$notify({ type: 'success', text: 'Cobrança gerada com sucesso' })
              })
          }
          return api.sale.payPix(sale.id).then((result) => {
            vm.createdSale = result?.sale || sale
            vm.pix = result?.pix || null
            vm.step = 'pix'
            vm.startPoll()
            vm.$notify({ type: 'info', text: 'Pix gerado — aguardando pagamento' })
          })
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => this.pollSaleOnce(), 4000)
      this.pollSaleOnce()
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    pollSaleOnce() {
      const vm = this
      const id = vm.createdSale?.id
      if (!id || vm.polling) return
      vm.polling = true
      return api.sale
        .get(id)
        .then((sale) => {
          if (!sale) return
          vm.createdSale = sale
          vm.pollStatus = sale.status
          if (sale.status === 'paid') {
            vm.stopPoll()
            vm.doneMessage = 'Pagamento confirmado. Venda paga.'
            vm.doneAlertType = 'success'
            vm.step = 'done'
            vm.$emit('paid', sale)
            vm.$notify({ type: 'success', text: 'Venda paga' })
          } else if (
            sale.status === 'canceled' ||
            sale.status === 'expired' ||
            sale.status === 'refunded'
          ) {
            vm.stopPoll()
            vm.doneMessage = `Venda ${sale.status}.`
            vm.doneAlertType = 'warning'
            vm.step = 'done'
          }
        })
        .catch(() => {})
        .finally(() => {
          vm.polling = false
        })
    },
    copyPix() {
      const text = this.pixCopyPaste
      if (!text || !navigator?.clipboard?.writeText) {
        this.$notify({ type: 'warn', text: 'Não foi possível copiar' })
        return
      }
      navigator.clipboard.writeText(text).then(
        () => this.$notify({ type: 'success', text: 'Código Pix copiado' }),
        () => this.$notify({ type: 'warn', text: 'Não foi possível copiar' })
      )
    },
  },
}
</script>
